import React, { useState, useEffect } from "react";
import { AiOutlineLoading } from "react-icons/ai";
import { Sparkles, Clock, Brain, MessageSquare, Plus, ChevronDown, ChevronUp } from "lucide-react";
import { getOpenAiRecommendations, requestOpenAiRecommendations, getNewReviewsCount, apiClient } from "../../api";
import { useProperty } from "../../Contexts/PropertyContext/PropertyContext";
import { baseUrl } from "../../Environments/environment";
import { toast } from "react-toastify";

const RecommendationsPage = () => {
  const [history, setHistory] = useState([]);
  const [selectedRec, setSelectedRec] = useState(null);
  const [propertyDetails, setPropertyDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetchingHistory, setFetchingHistory] = useState(false);
  const [isEditingContext, setIsEditingContext] = useState(false);
  const [newReviewsCount, setNewReviewsCount] = useState(0);
  const [editName, setEditName] = useState("");
  const [editBusinessType, setEditBusinessType] = useState("");
  const [editWebsite, setEditWebsite] = useState("");

  // Location
  const [editAddress, setEditAddress] = useState("");
  const [editCity, setEditCity] = useState("");
  const [editState, setEditState] = useState("");
  const [editCountry, setEditCountry] = useState("");
  const [editZipcode, setEditZipcode] = useState("");

  // Serialized context
  const [editYearsInBusiness, setEditYearsInBusiness] = useState("");
  const [editEmployeeCount, setEditEmployeeCount] = useState("");
  const [editBranchCount, setEditBranchCount] = useState("");
  const [editServices, setEditServices] = useState("");
  const [editTargetCustomers, setEditTargetCustomers] = useState("");
  const [editMonthlyCustomerVolume, setEditMonthlyCustomerVolume] = useState("");
  const [editBusinessGoals, setEditBusinessGoals] = useState("");
  const [editCurrentChallenges, setEditCurrentChallenges] = useState("");
  const [editBudgetLevel, setEditBudgetLevel] = useState("Medium");
  const [editConstraints, setEditConstraints] = useState("");
  const [editCompetitors, setEditCompetitors] = useState("");
  const [editOwnerNotes, setEditOwnerNotes] = useState("");
  const [editAboutDescription, setEditAboutDescription] = useState("");
  const [savingContext, setSavingContext] = useState(false);
  const [descriptionWarning, setDescriptionWarning] = useState("");
  const [ownerNotesWarning, setOwnerNotesWarning] = useState("");
  const { propertyName, selectedPropertyId } = useProperty();

  const propertyId = selectedPropertyId;

  // Load recommendation history for the property
  const fetchHistory = async () => {
    if (!propertyId) return;
    setFetchingHistory(true);
    try {
      const response = await getOpenAiRecommendations(propertyId);
      const items = response.data.result || [];
      setHistory(items);
    } catch (error) {
      console.error("Failed to fetch recommendation history", error);
      toast.error("Failed to fetch recommendation history");
    } finally {
      setFetchingHistory(false);
    }
  };

  // Load property details
  const fetchPropertyDetails = async () => {
    if (!propertyId) return;
    try {
      const response = await apiClient.get(`${baseUrl}/property/${propertyId}`);
      const propObj = response.data?.result?.property?.property;
      if (propObj) {
        setPropertyDetails(propObj);
      }
    } catch (error) {
      console.error("Failed to fetch property details", error);
    }
  };

  const fetchNewReviewsCount = async () => {
    if (!propertyId) return;
    try {
      const response = await getNewReviewsCount(propertyId);
      setNewReviewsCount(response.data?.result?.count || 0);
    } catch (error) {
      console.error("Failed to fetch new reviews count", error);
    }
  };

  useEffect(() => {
    if (propertyId) {
      fetchHistory();
      fetchPropertyDetails();
      fetchNewReviewsCount();
    }
  }, [propertyId]);

  // Request new recommendation run
  const handleRequestNew = async () => {
    setLoading(true);
    setIsEditingContext(false);
    try {
      const response = await requestOpenAiRecommendations(propertyId);
      const newRec = response.data.result;
      toast.success("New recommendations generated successfully!");
      setHistory((prev) => [newRec, ...prev]);
      setSelectedRec(newRec);
      fetchNewReviewsCount();
    } catch (error) {
      console.error("Failed to generate recommendations", error);
      toast.error(error.response?.data?.message || "Failed to generate recommendations. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Helper to sum up supportingReviewsCount from old data fallback
  const calculateTotalReviews = (rec) => {
    if (!rec.recommendations) return 0;
    return rec.recommendations.reduce((sum, item) => {
      if (typeof item === "object" && item.supportingReviewsCount) {
        return sum + Number(item.supportingReviewsCount);
      }
      return sum;
    }, 0);
  };

  const parseAboutText = (aboutText) => {
    const result = {
      yearsInBusiness: "",
      employeeCount: "",
      branchCount: "",
      services: "",
      targetCustomers: "",
      monthlyCustomerVolume: "",
      businessGoals: "",
      currentChallenges: "",
      budgetLevel: "Medium",
      constraints: "",
      competitors: "",
      ownerNotes: "",
      aboutDescription: "",
      monthsToAnalyze: 2,
      maxReviewsToAnalyze: 100
    };

    if (!aboutText) return result;

    const lines = aboutText.split("\n");
    lines.forEach(line => {
      if (line.startsWith("Years in Business: ")) {
        result.yearsInBusiness = line.replace("Years in Business: ", "").trim();
      } else if (line.startsWith("Employee Count: ")) {
        result.employeeCount = line.replace("Employee Count: ", "").trim();
      } else if (line.startsWith("Staff Size: ")) {
        result.employeeCount = line.replace("Staff Size: ", "").trim();
      } else if (line.startsWith("Branch Count: ")) {
        result.branchCount = line.replace("Branch Count: ", "").trim();
      } else if (line.startsWith("Services Offered: ")) {
        result.services = line.replace("Services Offered: ", "").trim();
      } else if (line.startsWith("Key Services/Specialties: ")) {
        result.services = line.replace("Key Services/Specialties: ", "").trim();
      } else if (line.startsWith("Target Customers: ")) {
        result.targetCustomers = line.replace("Target Customers: ", "").trim();
      } else if (line.startsWith("Monthly Customer Volume: ")) {
        result.monthlyCustomerVolume = line.replace("Monthly Customer Volume: ", "").trim();
      } else if (line.startsWith("Business Goals: ")) {
        result.businessGoals = line.replace("Business Goals: ", "").trim();
      } else if (line.startsWith("Current Challenges: ")) {
        result.currentChallenges = line.replace("Current Challenges: ", "").trim();
      } else if (line.startsWith("Budget Level: ")) {
        result.budgetLevel = line.replace("Budget Level: ", "").trim();
      } else if (line.startsWith("Constraints: ")) {
        result.constraints = line.replace("Constraints: ", "").trim();
      } else if (line.startsWith("Competitors: ")) {
        result.competitors = line.replace("Competitors: ", "").trim();
      } else if (line.startsWith("Owner Notes: ")) {
        result.ownerNotes = line.replace("Owner Notes: ", "").trim();
      } else if (line.startsWith("Months to Analyze: ")) {
        result.monthsToAnalyze = parseInt(line.replace("Months to Analyze: ", "").trim(), 10) || 2;
      } else if (line.startsWith("Max Reviews to Analyze: ")) {
        result.maxReviewsToAnalyze = parseInt(line.replace("Max Reviews to Analyze: ", "").trim(), 10) || 100;
      } else if (line.startsWith("Business Description: ")) {
        result.aboutDescription = line.replace("Business Description: ", "").trim();
      } else if (line.startsWith("Additional Summary: ")) {
        result.aboutDescription = line.replace("Additional Summary: ", "").trim();
      }
    });

    const hasAnyPrefix = Object.keys(result).some(k => k !== "budgetLevel" && k !== "monthsToAnalyze" && k !== "maxReviewsToAnalyze" && result[k]);
    if (!hasAnyPrefix) {
      result.aboutDescription = aboutText.trim();
    }

    return result;
  };

  useEffect(() => {
    if (propertyDetails) {
      setEditName(propertyDetails.name || "");
      setEditBusinessType(propertyDetails.businessType || "hotel");
      setEditWebsite(propertyDetails.website || "");
      setEditAddress(propertyDetails.address || "");
      setEditCity(propertyDetails.city || "");
      setEditState(propertyDetails.state || "");
      setEditCountry(propertyDetails.country || "");
      setEditZipcode(propertyDetails.zipcode || "");

      const parsed = parseAboutText(propertyDetails.about || "");
      setEditYearsInBusiness(parsed.yearsInBusiness);
      setEditEmployeeCount(parsed.employeeCount);
      setEditBranchCount(parsed.branchCount);
      setEditServices(parsed.services);
      setEditTargetCustomers(parsed.targetCustomers);
      setEditMonthlyCustomerVolume(parsed.monthlyCustomerVolume);
      setEditBusinessGoals(parsed.businessGoals);
      setEditCurrentChallenges(parsed.currentChallenges);
      setEditBudgetLevel(parsed.budgetLevel || "Medium");
      setEditConstraints(parsed.constraints);
      setEditCompetitors(parsed.competitors);
      setEditOwnerNotes(parsed.ownerNotes);
      setEditAboutDescription(parsed.aboutDescription);
    }
  }, [propertyDetails]);

  const getWordCount = (text) => {
    if (!text) return 0;
    return text.trim().split(/\s+/).filter(Boolean).length;
  };

  const handleDescriptionChange = (e) => {
    const value = e.target.value;
    const wordsArray = value.trim().split(/\s+/).filter(Boolean);
    const count = wordsArray.length;
    
    if (count <= 250) {
      setEditAboutDescription(value);
      setDescriptionWarning("");
    } else {
      const sliced = value.split(/\s+/).slice(0, 250).join(" ");
      setEditAboutDescription(sliced);
      setDescriptionWarning("You have already exceeded the limit!");
    }
  };

  const handleOwnerNotesChange = (e) => {
    const value = e.target.value;
    const wordsArray = value.trim().split(/\s+/).filter(Boolean);
    const count = wordsArray.length;
    
    if (count <= 150) {
      setEditOwnerNotes(value);
      setOwnerNotesWarning("");
    } else {
      const sliced = value.split(/\s+/).slice(0, 150).join(" ");
      setEditOwnerNotes(sliced);
      setOwnerNotesWarning("You have already exceeded the limit!");
    }
  };

  const handleSaveContext = async () => {
    setSavingContext(true);
    try {
      const combinedAbout = [
        editYearsInBusiness ? `Years in Business: ${editYearsInBusiness}` : "",
        editEmployeeCount ? `Employee Count: ${editEmployeeCount}` : "",
        editBranchCount ? `Branch Count: ${editBranchCount}` : "",
        editServices ? `Services Offered: ${editServices}` : "",
        editTargetCustomers ? `Target Customers: ${editTargetCustomers}` : "",
        editMonthlyCustomerVolume ? `Monthly Customer Volume: ${editMonthlyCustomerVolume}` : "",
        editBusinessGoals ? `Business Goals: ${editBusinessGoals}` : "",
        editCurrentChallenges ? `Current Challenges: ${editCurrentChallenges}` : "",
        `Budget Level: ${editBudgetLevel || "Medium"}`,
        editConstraints ? `Constraints: ${editConstraints}` : "",
        editCompetitors ? `Competitors: ${editCompetitors}` : "",
        editOwnerNotes ? `Owner Notes: ${editOwnerNotes}` : "",
        editAboutDescription ? `Business Description: ${editAboutDescription}` : ""
      ].filter(Boolean).join("\n");

      const formData = new FormData();
      formData.append("name", editName);
      formData.append("businessType", editBusinessType);
      formData.append("website", editWebsite);
      formData.append("address", editAddress);
      formData.append("city", editCity);
      formData.append("state", editState);
      formData.append("country", editCountry);
      formData.append("zipcode", editZipcode);
      formData.append("about", combinedAbout);

      const response = await apiClient.put(`${baseUrl}/property/${propertyId}`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      const updatedProp = response.data?.result?.property;
      if (updatedProp) {
        setPropertyDetails(updatedProp);
        toast.success("AI Context updated successfully!");
        setIsEditingContext(false);
      } else {
        await fetchPropertyDetails();
        toast.success("AI Context updated successfully!");
        setIsEditingContext(false);
      }
    } catch (error) {
      console.error("Failed to save AI Context:", error);
      toast.error("Failed to update AI Context. Please check details and try again.");
    } finally {
      setSavingContext(false);
    }
  };

  const filteredRecs = selectedRec?.recommendations || [];

  const [expandedCards, setExpandedCards] = useState({});

  useEffect(() => {
    setExpandedCards({});
  }, [selectedRec]);

  // Reset page scroll to the top when switching between chat and context editor, or changing recommendations
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [isEditingContext, selectedRec]);

  const toggleCard = (index) => {
    setExpandedCards(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const expandAll = () => {
    const allExpanded = {};
    filteredRecs.forEach((_, index) => {
      allExpanded[index] = true;
    });
    setExpandedCards(allExpanded);
  };

  const shrinkAll = () => {
    setExpandedCards({});
  };

  return (
    <div className="w-full flex flex-col md:flex-row text-gray-800 animate-scaleIn">

      <div className={`w-full md:w-60 bg-gray-50 flex flex-col md:sticky md:top-6 md:h-[calc(100vh-5.5rem)] border border-gray-200 rounded-lg flex-shrink-0 overflow-hidden shadow-sm md:mr-4 ${selectedRec !== null || isEditingContext ? 'hidden md:flex' : 'flex h-[calc(100vh-5.5rem)]'}`}>

        {/* New Run Button (ChatGPT style) */}
        <div className="p-4 flex flex-col gap-2 shrink-0">
          <button
            onClick={() => {
              setSelectedRec(null);
              setIsEditingContext(false);
            }}
            disabled={loading}
            className="w-full flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-md text-sm font-medium shadow-md hover:shadow-lg disabled:opacity-50 transition-[background-color,box-shadow] duration-150"
          >
            <Plus className="w-3.5 h-3.5" />
            <span>New Advisor Run</span>
          </button>
        </div>

        {/* History List */}
        <div className="flex-1 overflow-y-auto px-2 py-1 space-y-1">
          <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider px-3 mb-2 mt-2">
            Recent Runs
          </div>
          {fetchingHistory && history.length === 0 ? (
            <div className="flex items-center justify-center py-12 text-gray-400 text-xs gap-2 font-medium">
              <AiOutlineLoading className="animate-spin text-indigo-600" /> Loading...
            </div>
          ) : history.length === 0 ? (
            <div className="text-center py-12 text-gray-400 text-xs font-semibold">
              No runs found.
            </div>
          ) : (
            history.map((item) => {
              const isSelected = selectedRec?._id === item._id;
              const dateStr = new Date(item.createdAt).toLocaleString(undefined, {
                month: "short",
                day: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              });
              return (
                <button
                  key={item._id}
                  onClick={() => {
                    setSelectedRec(item);
                    setIsEditingContext(false);
                  }}
                  className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-md text-xs sm:text-sm transition-[background-color,border-color] duration-150 relative group ${isSelected
                    ? "bg-white text-indigo-600 font-semibold border-l-4 border-indigo-600 shadow-sm"
                    : "hover:bg-gray-200/50 text-gray-600 font-normal"
                    }`}
                >
                  <MessageSquare className={`w-3.5 h-3.5 shrink-0 ${isSelected ? "text-indigo-600" : "text-gray-400"}`} />
                  <span className="truncate pr-6">{dateStr}</span>
                  <span className={`absolute right-2 px-2 py-0.5 rounded-full text-xs font-medium ${isSelected ? "bg-indigo-50 text-indigo-700" : "bg-gray-200 text-gray-600"}`}>
                    {item.recommendations?.length || 0}
                  </span>
                </button>
              );
            })
          )}
        </div>

        {/* Sidebar Footer (ChatGPT Profile style) */}
        <div className="p-4 border-t border-gray-200 bg-gray-50/50 flex items-center justify-between gap-3 shrink-0">
          {isEditingContext ? (
            <div className="flex items-center gap-2 w-full">
              <button
                onClick={() => setIsEditingContext(false)}
                className="flex-1 px-3 py-2 rounded-md text-xs font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 shadow-sm transition-colors text-center"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveContext}
                disabled={savingContext}
                className="flex-1 bg-green-400 hover:bg-green-500 text-white px-3 py-2 rounded-md text-xs font-medium transition-colors flex items-center justify-center gap-1.5 shadow-sm disabled:opacity-60"
              >
                {savingContext && <AiOutlineLoading className="animate-spin text-xs" />}
                <span>Save</span>
              </button>
            </div>
          ) : (
            <>
              <div
                onClick={() => setIsEditingContext(true)}
                className="flex items-center gap-3 min-w-0 flex-1 cursor-pointer group/prop"
                title="Edit AI Context"
              >
                <div className="w-8 h-8 rounded-full bg-indigo-100 group-hover/prop:bg-indigo-200 flex items-center justify-center text-indigo-600 font-semibold text-sm shrink-0 shadow-sm border border-indigo-200 transition-colors">
                  {propertyName ? propertyName[0].toUpperCase() : "P"}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-xs text-gray-400 font-medium uppercase tracking-wider leading-none">Property</div>
                  <div className="text-sm font-semibold text-gray-700 group-hover/prop:text-indigo-600 truncate mt-1.5 transition-colors">
                    {propertyName || "Advisor Workspace"}
                  </div>
                </div>
              </div>
              <button
                onClick={() => setIsEditingContext(true)}
                className="text-gray-400 hover:text-indigo-600 hover:bg-gray-200 p-1.5 rounded-lg transition-colors shrink-0"
                title="Edit AI Context"
              >
                <Sparkles className="w-4 h-4 text-indigo-600" />
              </button>
            </>
          )}
        </div>

      </div>

      {/* Right Content Panel */}
      <div className={`flex-1 flex flex-col border border-gray-200 rounded-lg bg-white shadow-sm animate-fadeIn ${selectedRec === null && !isEditingContext ? 'hidden md:flex' : 'flex min-h-[calc(100vh-5.5rem)] md:min-h-[60vh]'}`}>

        {/* Top Bar (ChatGPT model header style) */}
        <div className="flex justify-between items-center px-6 py-3.5 bg-white border-b border-gray-200 shrink-0">
          <div className="flex items-center gap-3">
            {(selectedRec !== null || isEditingContext) && (
              <button
                onClick={() => {
                  setSelectedRec(null);
                  setIsEditingContext(false);
                }}
                className="md:hidden flex items-center gap-1 text-indigo-600 hover:text-indigo-700 text-sm font-medium mr-2"
              >
                &larr; Runs
              </button>
            )}
            <h3 className="text-base font-semibold text-gray-900">
              {isEditingContext ? "Context Configuration" : "Reputation Advisor"}
            </h3>
            {!isEditingContext && selectedRec?.model && (
              <span className="bg-indigo-50 border border-indigo-100 text-indigo-700 text-xs font-medium px-2.5 py-0.5 rounded-full shadow-sm">
                {selectedRec.model}
              </span>
            )}
          </div>
          {isEditingContext && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => setIsEditingContext(false)}
                className="px-3.5 py-2 rounded-md text-xs sm:text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 shadow-sm transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveContext}
                disabled={savingContext}
                className="bg-green-400 hover:bg-green-500 text-white px-3.5 py-2 rounded-md text-xs sm:text-sm font-medium transition-colors flex items-center gap-1.5 shadow-sm disabled:opacity-60"
              >
                {savingContext && <AiOutlineLoading className="animate-spin text-xs" />}
                Save Details
              </button>
            </div>
          )}
        </div>

        {isEditingContext ? (
          /* Full-screen Editor View on the right panel - printed directly on the modal background (white) */
          <div className="flex-1 flex flex-col justify-between bg-white px-8 py-6 animate-scaleIn">



            {/* Form Container */}
            <div className="flex-1 pr-2 my-4 w-full">
              <div className="max-w-2xl mx-auto w-full space-y-6">

                {/* Section 1: General Info */}
                <div>
                  <span className="text-xs sm:text-sm font-semibold text-gray-900 uppercase tracking-wider block border-b border-gray-200 pb-1.5 mb-3 mt-2">
                    General Information
                  </span>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-0">
                        Business Name
                      </label>
                      <input
                        type="text"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        placeholder="Enter business name..."
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 px-3 sm:px-4 py-2 text-base sm:text-sm bg-white"
                      />
                    </div>
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-0">
                        Business Category
                      </label>
                      <select
                        value={editBusinessType}
                        onChange={(e) => setEditBusinessType(e.target.value)}
                        className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:border-indigo-500 focus:ring-indigo-500 text-black text-base sm:text-sm mt-1 bg-white cursor-pointer"
                      >
                        <option value="hotel">Hotel</option>
                        <option value="motel">Motel</option>
                        <option value="extended_stay_hotel">Extended Stay Hotel</option>
                        <option value="budget_hotel">Budget Hotel</option>
                        <option value="inn">Inn</option>
                        <option value="hostel">Hostel</option>
                        <option value="resort_hotel">Resort Hotel</option>
                        <option value="restaurant">Restaurant</option>
                        <option value="dentist">Dentist</option>
                        <option value="chiropractor">Chiropractor</option>
                        <option value="urgent_care_center">Urgent Care Center</option>
                        <option value="spa">Spa</option>
                        <option value="beauty_salon">Beauty Salon</option>
                        <option value="lawyer">Lawyer / Attorney</option>
                        <option value="auto_repair_shop">Auto Repair Shop</option>
                        <option value="business_consultant">Business Consultant</option>
                        {propertyDetails?.businessType && !["hotel", "motel", "extended_stay_hotel", "budget_hotel", "inn", "hostel", "resort_hotel", "restaurant", "dentist", "chiropractor", "urgent_care_center", "spa", "beauty_salon", "lawyer", "auto_repair_shop", "business_consultant"].includes(propertyDetails.businessType) && (
                          <option value={propertyDetails.businessType}>{propertyDetails.businessType.replace(/_/g, ' ')}</option>
                        )}
                      </select>
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-0">
                        Website
                      </label>
                      <input
                        type="url"
                        value={editWebsite}
                        onChange={(e) => setEditWebsite(e.target.value)}
                        placeholder="https://example.com"
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 px-3 sm:px-4 py-2 text-base sm:text-sm bg-white"
                      />
                    </div>
                  </div>
                </div>

                {/* Section 2: Location */}
                <div className="pt-2">
                  <span className="text-xs sm:text-sm font-semibold text-gray-900 uppercase tracking-wider block border-b border-gray-200 pb-1.5 mb-3 mt-2">
                    Location
                  </span>
                  <div className="grid grid-cols-1 gap-4">
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-0">
                        Address
                      </label>
                      <input
                        type="text"
                        value={editAddress}
                        onChange={(e) => setEditAddress(e.target.value)}
                        placeholder="Street Address..."
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 px-3 sm:px-4 py-2 text-base sm:text-sm bg-white"
                      />
                    </div>
                  </div>
                </div>

                {/* Section 3: Operations */}
                <div className="pt-2">
                  <span className="text-xs sm:text-sm font-semibold text-gray-900 uppercase tracking-wider block border-b border-gray-200 pb-1.5 mb-3 mt-2">
                    Operations
                  </span>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-0">
                        Years in Operation
                      </label>
                      <input
                        type="number"
                        min="0"
                        value={editYearsInBusiness}
                        onChange={(e) => setEditYearsInBusiness(e.target.value)}
                        placeholder="Years..."
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 px-3 sm:px-4 py-2 text-base sm:text-sm bg-white"
                      />
                    </div>
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-0">
                        Staff Size
                      </label>
                      <input
                        type="number"
                        min="0"
                        value={editEmployeeCount}
                        onChange={(e) => setEditEmployeeCount(e.target.value)}
                        placeholder="Number of staff..."
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 px-3 sm:px-4 py-2 text-base sm:text-sm bg-white"
                      />
                    </div>
                  </div>
                </div>


                {/* Section 6: Business Description & Notes */}
                <div className="pt-2">
                  <span className="text-xs sm:text-sm font-semibold text-gray-900 uppercase tracking-wider block border-b border-gray-200 pb-1.5 mb-3 mt-2">
                    Description & Consultant Notes
                  </span>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-0">
                        Business Description / About
                      </label>
                      <textarea
                        rows={3}
                        value={editAboutDescription}
                        onChange={handleDescriptionChange}
                        placeholder="Provide a general overview of your business mission or background..."
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 px-3 sm:px-4 py-2 text-base sm:text-sm bg-white resize-none"
                      />
                      <div className="flex justify-between items-center mt-1">
                        <span className="text-xs text-gray-500 font-normal">Limit: 250 words maximum</span>
                        <span className={`text-xs font-semibold ${getWordCount(editAboutDescription) >= 250 ? 'text-amber-600 font-bold' : 'text-gray-500'}`}>
                          {getWordCount(editAboutDescription)} / 250 words
                        </span>
                      </div>
                      {descriptionWarning && (
                        <div className="text-xs text-red-600 font-semibold mt-1">
                          {descriptionWarning}
                        </div>
                      )}
                    </div>
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-0">
                        Owner Notes (Internal context for the AI consultant)
                      </label>
                      <textarea
                        rows={2}
                        value={editOwnerNotes}
                        onChange={handleOwnerNotesChange}
                        placeholder="e.g. Focus particularly on cleaning complaints, ignore billing comments..."
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 px-3 sm:px-4 py-2 text-base sm:text-sm bg-white resize-none"
                      />
                      <div className="flex justify-between items-center mt-1">
                        <span className="text-xs text-gray-500 font-normal">Limit: 150 words maximum</span>
                        <span className={`text-xs font-semibold ${getWordCount(editOwnerNotes) >= 150 ? 'text-amber-600 font-bold' : 'text-gray-500'}`}>
                          {getWordCount(editOwnerNotes)} / 150 words
                        </span>
                      </div>
                      {ownerNotesWarning && (
                        <div className="text-xs text-red-600 font-semibold mt-1">
                          {ownerNotesWarning}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

              </div>
            </div>



          </div>
        ) : (
          /* Recommendations Chat view */
          <>
            {/* Chat-like Conversation Thread */}
            <div className="flex-1 bg-gray-50/20 flex flex-col divide-y divide-gray-100 animate-fadeIn">
              {loading ? (
                <div className="flex-1 flex flex-col">
                  {/* Assistant loading block */}
                  <div className="px-6 py-6 bg-gray-50/50 flex-1">
                    <div className="max-w-4xl mx-auto flex gap-5">
                      <div className="w-8 h-8 rounded-full bg-indigo-600 text-white flex items-center justify-center shrink-0 shadow-sm">
                        <Brain className="w-4 h-4" />
                      </div>
                      <div className="flex-1 pt-0.5">
                        <div className="text-xs font-semibold text-indigo-600 uppercase tracking-wider flex items-center gap-1.5">
                          <span>Advisor</span>
                          <span className="w-1.5 h-1.5 rounded-full bg-indigo-600 animate-ping"></span>
                        </div>
                        <div className="mt-5 space-y-4">
                          <div className="flex items-center gap-2 text-gray-500 text-xs sm:text-sm font-medium">
                            <AiOutlineLoading className="animate-spin text-indigo-600" />
                            <span>Analyzing guest reviews via OpenAI...</span>
                          </div>
                          <div className="h-4 bg-gray-200 rounded-lg w-3/4 animate-pulse"></div>
                          <div className="h-4 bg-gray-200 rounded-lg w-5/6 animate-pulse"></div>
                          <div className="h-4 bg-gray-200 rounded-lg w-2/3 animate-pulse"></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : selectedRec ? (
                <>
                  {/* Assistant Advisor Message Block */}
                  <div className="px-6 py-6 bg-gray-50/50 flex-1">
                    <div className="max-w-4xl mx-auto flex gap-5">
                      <div className="w-8 h-8 rounded-full bg-indigo-600 text-white flex items-center justify-center shrink-0 shadow-sm">
                        <Brain className="w-4 h-4" />
                      </div>
                      <div className="flex-1 pt-0.5">
                        <div className="text-xs font-semibold text-indigo-600 uppercase tracking-wider mb-4">Reputation Advisor</div>



                        {/* AI Recommendations List */}
                        <div className="space-y-4">

                          {/* List header with Expand/Shrink actions */}
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2.5 pl-1 mb-3">
                            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                              AI Recommendations
                            </div>
                            <div className="flex flex-wrap items-center gap-3">
                              {/* Expand/Shrink buttons */}
                              <div className="flex items-center gap-1.5">
                                <button
                                  onClick={expandAll}
                                  className="text-xs font-medium text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 px-2 py-1.5 rounded-md border border-indigo-100 flex items-center gap-1 transition-colors"
                                  title="Expand All"
                                >
                                  <ChevronDown className="w-3.5 h-3.5" />
                                  <span>Expand All</span>
                                </button>
                                <button
                                  onClick={shrinkAll}
                                  className="text-xs font-medium text-gray-600 hover:text-gray-700 hover:bg-gray-100 px-2 py-1.5 rounded-md border border-gray-200 flex items-center gap-1 transition-colors"
                                  title="Shrink All"
                                >
                                  <ChevronUp className="w-3.5 h-3.5" />
                                  <span>Shrink All</span>
                                </button>
                              </div>
                            </div>
                          </div>

                          {/* Recommendation cards (with increased font sizes) */}
                          <div className="space-y-3">
                            {filteredRecs.length > 0 ? (
                              filteredRecs.map((rec, i) => {
                                const isObject = typeof rec === "object" && rec !== null;
                                const isNewFormat = isObject && Array.isArray(rec.reviews);
                                const title = isNewFormat ? rec.issue : (isObject ? (rec.title || `Recommendation ${i + 1}`) : `Recommendation ${i + 1}`);
                                const description = isObject ? rec.description : rec;
                                const supportingReviews = isObject ? rec.supportingReviewsCount : null;
                                const hasIssueAndSolution = isObject && (rec.issue || rec.solution);

                                const cardBorder = isNewFormat ? "border-gray-200" : "border-l-4 border-l-indigo-400 border-gray-200";
                                const numberStyle = isNewFormat ? "bg-gray-100 border-gray-200 text-gray-700" : "bg-indigo-50 border-indigo-100 text-indigo-600";
                                const badgeStyle = isNewFormat ? "bg-gray-100 border-gray-200 text-gray-600" : "bg-red-50 border-red-100 text-red-600";

                                return (
                                  <div
                                    key={i}
                                    className={`bg-white border rounded-lg p-5 shadow-sm hover:shadow-md transition-[box-shadow] duration-200 flex flex-col gap-4 ${cardBorder}`}
                                  >
                                    {/* Header Row */}
                                    <div
                                      onClick={() => toggleCard(i)}
                                      className="flex items-start justify-between gap-4 cursor-pointer w-full"
                                    >
                                      <div className="flex items-start gap-4 min-w-0 flex-1">
                                        {/* Icon number */}
                                        <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-semibold shrink-0 shadow-sm ${numberStyle}`}>
                                          {i + 1}
                                        </div>
                                        <div className="min-w-0 flex-1 pt-0.5">
                                          <h5 className="text-sm sm:text-base font-semibold text-gray-900 pr-2">
                                            {title}
                                          </h5>
                                        </div>
                                      </div>

                                      {/* Reviews count + Expand Button */}
                                      <div className="shrink-0 flex items-center gap-2">
                                        {supportingReviews !== null && supportingReviews > 0 && (
                                          <span className={`hidden sm:flex items-center gap-1 border text-xs font-semibold px-2 py-1 rounded-full ${badgeStyle}`}>
                                            <MessageSquare className="w-3 h-3" />
                                            {supportingReviews} {supportingReviews === 1 ? 'review' : 'reviews'}
                                          </span>
                                        )}
                                        <div className="text-gray-400 hover:text-indigo-600 p-1.5 rounded-md transition-colors bg-gray-50 border border-gray-200 flex items-center justify-center">
                                          {expandedCards[i] ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                                        </div>
                                      </div>
                                    </div>


                                    {/* Unfolded Content */}
                                    {expandedCards[i] && (
                                      <div className="border-t border-gray-100 pt-4 w-full animate-fadeIn space-y-4">
                                        {isNewFormat ? (
                                          <div className="space-y-3 pt-2">
                                            {rec.reviews && rec.reviews.map((rev, revIdx) => (
                                              <div key={revIdx} className="border border-gray-200 rounded-lg p-3.5 bg-white">
                                                <p className="text-sm text-gray-800 leading-relaxed font-normal">
                                                  "{rev.comments || "~ no text ~"}"
                                                </p>
                                                <div className="mt-2 text-xs text-gray-500 flex items-center gap-2">
                                                  <span>Rating: {rev.overAllRating} ★</span>
                                                  <span>•</span>
                                                  <span>{new Date(rev.createdAt).toLocaleDateString()}</span>
                                                </div>
                                              </div>
                                            ))}
                                          </div>
                                        ) : hasIssueAndSolution ? (
                                          <div className="space-y-3">
                                            {rec.issue && (
                                              <div className="bg-red-50/25 border border-red-100 rounded-lg p-3.5 shadow-sm">
                                                <div className="flex items-center gap-1.5 mb-1.5">
                                                  <span className="w-1.5 h-1.5 rounded-full bg-red-400" />
                                                  <span className="text-xs font-semibold text-red-600 uppercase tracking-wider">Issue</span>
                                                </div>
                                                <p className="text-xs sm:text-sm text-gray-600 font-normal leading-relaxed">
                                                  {rec.issue}
                                                </p>
                                              </div>
                                            )}
                                            {isObject && rec.rootCause && (
                                              <div className="bg-amber-50/25 border border-amber-100 rounded-lg p-3.5 shadow-sm">
                                                <div className="flex items-center gap-1.5 mb-1.5">
                                                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />
                                                  <span className="text-xs font-semibold text-amber-600 uppercase tracking-wider">Operational Root Cause</span>
                                                </div>
                                                <p className="text-xs sm:text-sm text-gray-600 font-normal leading-relaxed">
                                                  {rec.rootCause}
                                                </p>
                                              </div>
                                            )}
                                            {rec.solution && (
                                              <div className="bg-emerald-50/25 border border-emerald-100 rounded-lg p-3.5 shadow-sm">
                                                <div className="flex items-center gap-1.5 mb-1.5">
                                                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                                                  <span className="text-xs font-semibold text-emerald-600 uppercase tracking-wider">Suggested Action</span>
                                                </div>
                                                <p className="text-xs sm:text-sm text-gray-600 font-normal leading-relaxed">
                                                  {rec.solution}
                                                </p>
                                              </div>
                                            )}
                                            {isObject && (rec.businessImpact || rec.expectedBenefit) && (
                                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                {rec.businessImpact && (
                                                  <div className="bg-indigo-50/20 border border-indigo-100 rounded-lg p-3 shadow-sm">
                                                    <div className="flex items-center gap-1.5 mb-1">
                                                      <span className="w-1.5 h-1.5 rounded-full bg-indigo-400" />
                                                      <span className="text-xs font-semibold text-indigo-600 uppercase tracking-wider">Business Impact</span>
                                                    </div>
                                                    <p className="text-xs sm:text-sm text-gray-600 font-normal leading-relaxed">
                                                      {rec.businessImpact}
                                                    </p>
                                                  </div>
                                                )}
                                                {rec.expectedBenefit && (
                                                  <div className="bg-purple-50/20 border border-purple-100 rounded-lg p-3 shadow-sm">
                                                    <div className="flex items-center gap-1.5 mb-1">
                                                      <span className="w-1.5 h-1.5 rounded-full bg-purple-400" />
                                                      <span className="text-xs font-semibold text-purple-600 uppercase tracking-wider">Expected Benefit</span>
                                                    </div>
                                                    <p className="text-xs sm:text-sm text-gray-600 font-normal leading-relaxed">
                                                      {rec.expectedBenefit}
                                                    </p>
                                                  </div>
                                                )}
                                              </div>
                                            )}
                                          </div>
                                        ) : (
                                          <p className="text-xs sm:text-sm text-gray-600 font-normal mt-2 leading-relaxed">
                                            {description}
                                          </p>
                                        )}

                                      </div>
                                    )}
                                  </div>
                                );
                              })
                            ) : (
                              <div className="text-center py-12 bg-white border border-gray-200 rounded-lg text-gray-500 text-xs font-semibold">
                                No recommendations available for this run.
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Stats Sub-Panel inside thread */}
                        <div className="mt-8 pt-5 border-t border-gray-200 flex flex-wrap items-center justify-between gap-4 text-xs font-medium text-gray-500">
                          <div className="flex items-center gap-1.5">
                            <Clock className="w-3.5 h-3.5 text-gray-400" />
                            <span>Generated: {new Date(selectedRec.createdAt).toLocaleString()}</span>
                          </div>
                          <div className="flex items-center gap-2">
                             <span className="bg-gray-200/50 text-gray-500 px-2.5 py-1.5 rounded-md">
                              Input: {selectedRec.inputTokens || 0}
                            </span>
                            <span className="bg-gray-200/50 text-gray-500 px-2.5 py-1.5 rounded-md">
                              Output: {selectedRec.outputTokens || 0}
                            </span>
                            <span className="bg-emerald-50 border border-emerald-100 text-emerald-700 px-2.5 py-1.5 rounded-md">
                              Cost: ${Number(selectedRec.costUSD || 0).toFixed(4)}
                            </span>
                            {selectedRec.creditsUsed > 0 && (
                              <span className="bg-indigo-50 border border-indigo-100 text-indigo-700 px-2.5 py-1.5 rounded-md font-semibold">
                                🪙 {selectedRec.creditsUsed} credits used
                              </span>
                            )}
                          </div>
                        </div>

                      </div>
                    </div>
                  </div>
                </>
              ) : (
                /* ChatGPT Welcome/Start page */
                <div className="flex-1 flex flex-col items-center justify-center p-6 sm:p-10 max-w-2xl mx-auto w-full animate-fadeIn">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-tr from-indigo-500 to-purple-600 flex items-center justify-center text-white mb-6 shadow-md">
                    <Brain className="w-8 h-8 animate-pulse" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-950 mb-3 tracking-tight">
                    AI Reputation Advisor
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed text-center font-normal mb-8 max-w-lg">
                    Discover actionable insights from your reviews. The Reputation Advisor analyzes negative guest reviews (ratings ≤ 3 stars), isolates root causes, estimates business impact, and recommends operational steps.
                  </p>

                  {/* Configuration Form card */}
                  <div className="bg-white border border-gray-200 rounded-xl p-6 w-full shadow-sm space-y-5 mb-8">
                    <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider border-b border-gray-100 pb-2.5">
                      Advisor Run Context
                    </div>
                    <p className="text-xs text-gray-500 leading-relaxed font-normal">
                      The run will analyze all pending negative reviews (ratings ≤ 3 stars) from the last 3 months.
                    </p>
                    <div className="flex items-center justify-between text-sm pt-2 border-t border-gray-100">
                      <span className="text-gray-600 font-medium">New negative reviews available:</span>
                      <span className={`font-semibold px-2.5 py-1 rounded-full text-xs ${
                        newReviewsCount > 0 
                          ? 'bg-emerald-50 text-emerald-700 border border-emerald-100' 
                          : 'bg-red-50 text-red-700 border border-red-100'
                      }`}>
                        {newReviewsCount} new {newReviewsCount === 1 ? 'review' : 'reviews'}
                      </span>
                    </div>
                  </div>

                  <button
                    onClick={() => handleRequestNew()}
                    disabled={loading || newReviewsCount === 0}
                    className="flex items-center justify-center gap-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white px-7 py-3.5 rounded-lg text-sm font-semibold shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 w-full sm:w-auto"
                  >
                    <Sparkles className="w-4 h-4 text-white" />
                    <span>Generate Recommendations</span>
                  </button>
                </div>
              )}
            </div>
          </>
        )}

      </div>

      <style>{`
        @keyframes scaleIn {
          from {
            transform: scale(0.98);
            opacity: 0;
          }
          to {
            transform: scale(1);
            opacity: 1;
          }
        }
        .animate-scaleIn {
          animation: scaleIn 0.25s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }
      `}</style>
    </div>
  );
};

export default RecommendationsPage;
